import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import useUpdate from "../../../../api/useUpdate";
import useFetchObjects from "../../../../api/useFetchObjects";
import { Months, years } from "../../../../utils";
import { useTranslation } from "react-i18next";

const PayrollEdit = ({ payroll, onClose, token }) => {
  const { t } = useTranslation();
  const { data: employeeData, isLoading: loadingEmployees } = useFetchObjects(
    ["users"],
    "users",
    token
  );
  const [input, setInput] = useState({
    employee: payroll?.employee || "",
    total_salary: payroll?.total_salary || "",
    month: payroll?.month || "",
    year: payroll?.year || "",
    paid_salary: payroll?.paid_salary || "",
    retention_amount: payroll?.retention_amount || "",
    remaining_salary: payroll?.remaining_salary || "",
    date: payroll?.date || "",
  });
  const [formErrors, setFormErrors] = useState({});
  const { handleUpdate, isLoading: updating } = useUpdate(
    "employee-payroll",
    token
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInput({ ...input, [name]: value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    let errors = {};
    let isValid = true;

    if (!input.employee) {
      errors.employee = t("hr.employeeRequired");
      isValid = false;
    }

    if (!input.total_salary) {
      errors.total_salary = t("hr.totalSalaryRequired");
      isValid = false;
    }

    if (!input.month) {
      errors.month = t("hr.monthRequired");
      isValid = false;
    }

    if (!input.year) {
      errors.year = t("hr.yearRequired");
      isValid = false;
    }

    if (!input.paid_salary) {
      errors.paid_salary = t("hr.paidSalaryRequired");
      isValid = false;
    }

    setFormErrors(errors);

    if (isValid) {
      const formData = new FormData();
      formData.append("employee", input.employee);
      formData.append("total_salary", input.total_salary);
      formData.append("month", input.month);
      formData.append("year", input.year);
      formData.append("paid_salary", input.paid_salary);
      formData.append("retention_amount", input.retention_amount);
      formData.append("remaining_salary", input.remaining_salary);
      formData.append("date", input.date);
      handleUpdate(payroll.id, formData);
      onClose();
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      {loadingEmployees ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          sx={{ py: 3 }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TextField
            select
            fullWidth
            label={t("hr.employee")}
            name="employee"
            size="small"
            margin="normal"
            value={input.employee}
            onChange={handleChange}
            error={!!formErrors.employee}
            helperText={formErrors.employee}
            disabled={loadingEmployees}
          >
            {employeeData?.map((employee) => (
              <MenuItem key={employee.id} value={employee.id}>
                {employee.first_name} {employee.last_name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            fullWidth
            label={t("hr.totalSalary")}
            name="total_salary"
            margin="normal"
            size="small"
            error={!!formErrors.total_salary}
            helperText={formErrors.total_salary}
            value={input.total_salary}
            onChange={handleChange}
          />

          <TextField
            fullWidth
            select
            label={t("hr.month")}
            name="month"
            margin="normal"
            size="small"
            value={input.month}
            onChange={handleChange}
            error={!!formErrors.month}
            helperText={formErrors.month}
          >
            {Months.map((monthOption) => (
              <MenuItem key={monthOption.id} value={monthOption.id}>
                {monthOption.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            fullWidth
            select
            label={t("hr.year")}
            name="year"
            margin="normal"
            size="small"
            value={input.year}
            onChange={handleChange}
            error={!!formErrors.year}
            helperText={formErrors.year}
          >
            {years.map((yearOption) => (
              <MenuItem key={yearOption} value={yearOption}>
                {yearOption}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            fullWidth
            label={t("hr.paidSalary")}
            name="paid_salary"
            margin="normal"
            size="small"
            error={!!formErrors.paid_salary}
            helperText={formErrors.paid_salary}
            value={input.paid_salary}
            onChange={handleChange}
          />

          <TextField
            fullWidth
            label={t("hr.retentionAmount")}
            name="retention_amount"
            margin="normal"
            size="small"
            value={input.retention_amount}
            onChange={handleChange}
          />

          <TextField
            fullWidth
            label={t("hr.remainingSalary")}
            name="remaining_salary"
            margin="normal"
            size="small"
            value={input.remaining_salary}
            disabled
            helperText={t("hr.automaticallyBasedTotalSalary")}
          />

          <TextField
            fullWidth
            name="date"
            type="date"
            label={t("hr.date")}
            margin="normal"
            size="small"
            InputLabelProps={{ shrink: true }}
            error={!!formErrors.date}
            helperText={formErrors.date}
            value={input.date}
            onChange={handleChange}
          />

          <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
            <Button onClick={onClose} variant="outlined" size="small">
              {t("hr.cancel")}
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              size="small"
              sx={{ backgroundColor: "green", color: "white" }}
              disabled={updating}
            >
              {updating ? <CircularProgress size={20} /> : t("hr.save")}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default PayrollEdit;
